import { useMemo, useState } from 'react';
import { INTERNAL_CLIENT_OPTIONS } from '../data/knowledgeBase';
import { exportPreparationToPdf } from '../services/pdfService';
import { preparationService } from '../services/preparationService';
import type { ClientType, CustomerBehaviorProfile, FoodCategory, Market, PreparationInput, PreparationResult } from '../types';

interface UsePreparationState {
  customerProfile: CustomerBehaviorProfile;
  setCustomerProfile: (value: CustomerBehaviorProfile) => void;
  category: FoodCategory;
  setCategory: (value: FoodCategory) => void;
  market: Market;
  setMarket: (value: Market) => void;
  clientType: ClientType;
  setClientType: (value: ClientType) => void;
  scenario: string;
  setScenario: (value: string) => void;
  result: PreparationResult | null;
  loading: boolean;
  error: string | null;
  notice: string | null;
  generate: () => Promise<void>;
  clear: () => void;
  exportPdf: () => Promise<void>;
  canExport: boolean;
}

const MIN_SCENARIO_LENGTH = 50;

export const usePreparation = (): UsePreparationState => {
  const [customerProfile, setCustomerProfile] = useState<CustomerBehaviorProfile>('analitico');
  const [category, setCategory] = useState<FoodCategory>('aves');
  const [market, setMarket] = useState<Market>('interno');
  const [clientType, setClientType] = useState<ClientType>(INTERNAL_CLIENT_OPTIONS[0]);
  const [scenario, setScenario] = useState('');
  const [result, setResult] = useState<PreparationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const handleMarketChange = (value: Market): void => {
    setMarket(value);
    setClientType(value === 'interno' ? INTERNAL_CLIENT_OPTIONS[0] : 'distribuidor_importador');
  };

  const generate = async (): Promise<void> => {
    const cleanScenario = scenario.trim();
    if (!cleanScenario) {
      setError('Escreva o cenário da reunião para gerar a preparação comercial.');
      setNotice(null);
      return;
    }

    if (cleanScenario.length < MIN_SCENARIO_LENGTH) {
      setError('Detalhe um pouco mais o cenário para o motor conseguir construir OPC, SPIN, EPA e Senoide de forma específica.');
      setNotice(null);
      return;
    }

    setLoading(true);
    setError(null);
    setNotice(null);

    try {
      const input: PreparationInput = {
        customerProfile,
        category,
        market,
        clientType,
        scenario: cleanScenario
      };
      const generated = await preparationService.generate(input);
      setResult(generated);
      setNotice('Preparação gerada com sucesso. Revise os blocos e exporte se quiser levar para a reunião.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Não foi possível gerar a preparação agora.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const clear = (): void => {
    setCustomerProfile('analitico');
    setCategory('aves');
    handleMarketChange('interno');
    setScenario('');
    setResult(null);
    setError(null);
    setNotice(null);
  };

  const exportPdf = async (): Promise<void> => {
    if (!result) {
      setError('Gere uma preparação antes de exportar o PDF.');
      setNotice(null);
      return;
    }

    try {
      await exportPreparationToPdf(result);
      setNotice('PDF exportado com sucesso.');
      setError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Não foi possível exportar o PDF agora.';
      setError(message);
      setNotice(null);
    }
  };

  return {
    customerProfile,
    setCustomerProfile,
    category,
    setCategory,
    market,
    setMarket: handleMarketChange,
    clientType,
    setClientType,
    scenario,
    setScenario,
    result,
    loading,
    error,
    notice,
    generate,
    clear,
    exportPdf,
    canExport: useMemo(() => Boolean(result), [result])
  };
};
